import {
  useQuery,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { ReactNode } from "react";

import ApiErrorBanner from "components/ApiErrorBanner";
import Spinner from "components/ui/Spinner";
import type { ErrorMessages } from "services/errors";

// The fallback reads only `isError`, `error` and `refetch`, which every result shape
// carries — so an infinite query goes through it as readily as a plain one.
type AnyResult =
  | UseQueryResult<unknown, unknown>
  | UseInfiniteQueryResult<unknown, unknown>
  | UseSuspenseQueryResult<unknown, unknown>;

type FallbackProps = {
  results: AnyResult | readonly AnyResult[];
  messages?: ErrorMessages;
};

/**
 * The not-ready half of reading a query: a spinner while it is pending, and the
 * errors with a retry once it has failed.
 *
 * It takes results and hands nothing back, because the caller branches on
 * `isSuccess` and reads `data` through react-query's own narrowing. Reach for it
 * where the component composes the loading state itself — several queries, an
 * infinite one, or data that an effect or a sibling reads too. For a subtree that
 * is nothing but the query, `QueryLoader` does the whole job.
 */
export function QueryFallback(props: FallbackProps) {
  const failures = [props.results].flat().filter(result => result.isError);

  // A failure outranks a pending sibling: waiting out the rest tells the reader
  // nothing they can act on.
  if (failures.length === 0) {
    return <Spinner placement="fill" />;
  }

  return failures.map((result, index) => (
    <ApiErrorBanner
      key={index}
      error={result.error}
      messages={props.messages}
      onRetry={() => void result.refetch()}
    />
  ));
}

type LoaderProps<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = {
  query: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  messages?: ErrorMessages;
  // Replaces the spinner, never a failure's banner. `null` renders nothing at all.
  fallback?: ReactNode;
  // `Exclude`, never `NonNullable`: a query's data may legitimately be null —
  // `getAdaptiveTest` is typed `… | null` and its caller branches on it.
  children: (data: Exclude<TData, undefined>) => ReactNode;
};

/**
 * One query, read and rendered: the spinner or its errors until it is ready, then
 * `children` with the data.
 *
 * Takes the query's options rather than its result, so the callsite is the factory
 * call alone and the query mounts only where its data is read.
 */
export default function QueryLoader<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(props: LoaderProps<TQueryFnData, TError, TData, TQueryKey>) {
  const query = useQuery(props.query);

  // Narrowing rules out `undefined`, which the generic cannot say for itself.
  if (query.isSuccess) return props.children(query.data as Exclude<TData, undefined>);

  // `isPending`, so a failure still reaches the banner below.
  if (query.isPending && props.fallback !== undefined) return props.fallback;

  return <QueryFallback results={query} messages={props.messages} />;
}
